import React, { ReactNode, useContext, useCallback } from "react";
import { IconButton, Button, Icon } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import styles from "./ModalPopup.module.css";
import { ModalContext } from "contexts/ModalContext";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import classNames from "classnames";
import GetAppRoundedIcon from "@mui/icons-material/GetAppRounded";
import { useDropzone } from "react-dropzone";
import { FMSThemeContext } from "contexts/FMSThemeContext";
import ExcelIcon from "assets/ExcelIcon";
import { Loading } from "components/loading/Loading";
import { isRoleAdmin, Role } from "types/Employee";
import { Auth } from "lib/Auth";

interface IProps {
  className: string;
  children: ReactNode;
  header: ReactNode;
  headerBackgroundColor?: string;
  headerTintColor?: string;
  onSubmit: () => void;
  isSubmitting?: boolean;
  showDatePicker?: boolean;
  selectedDate?: Date;
  handleDateChange?: (date: Date | null) => void;
  hideModal?: () => void;
  branchName?: string;
  isUploadDownloadApplicable?: boolean;
  handleDownloadTemplate?: () => void;
  handleUploadFile?: (files: File[]) => void;
  isDownloading?: boolean;
  submitText?: string;
}

export const ModalPopup: React.FC<IProps> = ({
  className,
  header: Header,
  headerBackgroundColor = "#f6f6f6",
  headerTintColor = "#4d4d4d",
  onSubmit,
  children,
  showDatePicker = true,
  isSubmitting = false,
  selectedDate,
  handleDateChange = () => {},
  hideModal,
  branchName,
  isUploadDownloadApplicable = false,
  handleDownloadTemplate = () => {},
  handleUploadFile = (files) => {},
  isDownloading = false,
  submitText = "Add",
}) => {
  const modalProps = useContext(ModalContext);
  const isBranchPresent = branchName && branchName.length > 0 ? true : false;

  const onDrop = useCallback(
    (acceptedFiles) => {
      handleUploadFile(acceptedFiles);
    },
    [handleUploadFile]
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    multiple: false,
  });

  const { primaryColor, primaryTextColor } = useContext(FMSThemeContext);
  let showUploadAndDownload: boolean;
  const userRole = Auth.getInstance().getUser()?.role;
  if (isUploadDownloadApplicable && userRole) {
    showUploadAndDownload =
      isRoleAdmin(userRole) || userRole === Role.SUPERVISOR;
  } else {
    showUploadAndDownload = false;
  }

  return (
    <div className={className}>
      <div
        className={
          isBranchPresent
            ? styles.modalTitleBoxWithBranch
            : styles.modalTitleBox
        }
        style={{
          backgroundColor: headerBackgroundColor,
        }}
      >
        {showDatePicker && (
          <div className={styles.date}>
            <DesktopDatePicker
              enableAccessibleFieldDOMStructure={false}
              views={["day"]}
              value={selectedDate}
              onChange={handleDateChange}
              disableFuture
              format="MMM dd"
              slotProps={{
                textField: {
                  variant: "outlined",
                  InputProps: {
                    className: classNames(styles.input, styles.widthDates),
                  },
                },
              }}
            />
          </div>
        )}
        <div
          className={styles.modalTitleText}
          style={{
            color: headerTintColor,
          }}
        >
          <>
            {isBranchPresent && (
              <div className={styles.branchName}>{branchName}</div>
            )}
            {Header}
          </>
        </div>
        <div
          className={styles.modalCloseIcon}
          style={{
            color: headerTintColor,
          }}
        >
          <IconButton
            name="close"
            color="inherit"
            onClick={() => {
              if (hideModal) {
                hideModal();
              } else {
                modalProps.hideModal();
              }
            }}
            size="large">
            <CloseRoundedIcon />
          </IconButton>
        </div>
      </div>
      <form onSubmit={onSubmit}>
        <div className={styles.content}>
          <div className={styles.modalBody}>{children}</div>
          <div className={styles.buttons}>
            <Button
              className={
                showUploadAndDownload ? styles.download : styles.downloadHidden
              }
              onClick={handleDownloadTemplate}
              startIcon={<GetAppRoundedIcon />}
            >
              <Loading text={"Template"} isLoading={isDownloading} />
            </Button>
            <button
              className={styles.modalButton}
              style={{ backgroundColor: primaryColor, color: primaryTextColor }}
            >
              <Loading text={submitText} isLoading={isSubmitting} />
            </button>

            <div
              style={{
                display: "inline",
                visibility: showUploadAndDownload ? "visible" : "hidden",
              }}
              {...getRootProps({ className: styles.dropzone })}
            >
              <input {...getInputProps()} />
              <Button
                className={
                  showUploadAndDownload ? styles.upload : styles.uploadHidden
                }
                startIcon={
                  <Icon>
                    <ExcelIcon />
                  </Icon>
                }
              >
                Upload
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};
